import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef
} from "@angular/core";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { BlockedE164NumberService } from "@onsip/common/services/api/resources/blockedE164Number/blocked-e164-number.service";
import { BlockedE164Number } from "@onsip/common/services/api/resources/blockedE164Number/blocked-e164-number";
import { E164CountryCodeService } from "@onsip/common/services/api/resources/e164CountryCode/e164-country-code.service";
import { E164CountryCode } from "@onsip/common/services/api/resources/e164CountryCode/e164-country-code";
import { map, filter, take, distinctUntilChanged, startWith } from "rxjs/operators";
import { BehaviorSubject, Subscription, combineLatest } from "rxjs";
import phoneNumberValidator from "@onsip/common/libraries/validators/phone-number-validators";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { MatDialog } from "@angular/material/dialog";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { views } from "@onsip/web/app/phone/views";

@Component({
  selector: "onsip-blocked-numbers",
  templateUrl: "./blocked-numbers.component.html",
  styleUrls: ["./blocked-numbers.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BlockedNumbersComponent implements OnInit, OnDestroy {
  blockedNumberForm = this.fb.group({
    country: [""],
    phoneNumber: ["", [phoneNumberValidator({ isInternational: true }), Validators.required]],
    countryCode: [""]
  });

  blockedNumbersList: Array<BlockedE164Number> = [];
  countryCodeList: Array<E164CountryCode> = [];
  countryCodeMap: Map<string, string> = new Map<string, string>();
  countryCodePlaceholder = "";
  splitNumbers = false;
  largeOrGreaterBreakpoint = false;
  isMobile = false;
  initBlockedColMax = 10;
  views = views;
  loadingStateComplete$ = new BehaviorSubject(true);
  initLoaded$ = combineLatest({
    loadingStateComplete: this.loadingStateComplete$,
    countryCode: this.countryCodeService.state.pipe(filter(state => !state.loading)),
    blockedNumber: this.blockedNumberService.orgState
  }).pipe(
    map(({ loadingStateComplete }) => loadingStateComplete && true),
    startWith(false)
  );

  protected unsubscriber = new Subscription();

  constructor(
    private blockedNumberService: BlockedE164NumberService,
    private countryCodeService: E164CountryCodeService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
    private breakpoint: BreakpointObserver,
    private fb: NonNullableFormBuilder
  ) {}

  ngOnInit(): void {
    this.setBreakpoints();
    this.getBlockedNumbers();
    this.getCountryCodes();
    this.setCountrySelectSub();
    this.setCountryCodePlaceholder();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  getBlockedNumbers(): void {
    this.blockedNumberService.blockedE164NumberBrowse();
    this.blockedNumberService.orgState.subscribe(blockedNumList => {
      this.blockedNumbersList = blockedNumList;
      this.cdRef.markForCheck();
    });
  }

  getCountryCodes(): void {
    this.countryCodeService.e164CountryCodeBrowse();
    this.countryCodeService.state
      .pipe(
        filter(state => !state.loading),
        map(({ state }) => Object.values(state))
      )
      .subscribe(countryCodes => {
        countryCodes.map(countryCodeObj => {
          this.countryCodeMap.set(countryCodeObj.country, countryCodeObj.code);
        });
        this.countryCodeList = countryCodes.sort((a, b) => (a.country > b.country ? 1 : -1));
        this.blockedNumberForm.controls.country.setValue("United States");
      });
  }

  setCountrySelectSub(): void {
    this.unsubscriber.add(
      this.blockedNumberForm.controls.country.valueChanges.subscribe(value => {
        this.blockedNumberForm.controls.countryCode.setValue(
          (this.countryCodeMap.has(value) && this.countryCodeMap.get(value)) || ""
        );
        this.countryCodePlaceholder =
          this.blockedNumberForm.controls.countryCode.value !== ""
            ? "+" + this.blockedNumberForm.controls.countryCode.value
            : "";
        this.blockedNumberForm.controls.phoneNumber.setValue(this.countryCodePlaceholder + " ");

        this.cdRef.markForCheck();
      })
    );
  }

  setBreakpoints(): void {
    this.unsubscriber.add(
      this.breakpoint
        .observe([Breakpoints.XSmall, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
        .subscribe(result => {
          if (result.matches) {
            if (result.breakpoints[Breakpoints.XSmall]) {
              this.isMobile = true;
              this.splitNumbers = false;
              this.largeOrGreaterBreakpoint = false;
            } else {
              // Medium, Large, XL Breakpoints
              this.isMobile = false;
              this.splitNumbers = true;
              if (result.breakpoints[Breakpoints.Large] || result.breakpoints[Breakpoints.XLarge]) {
                this.largeOrGreaterBreakpoint = true;
              } else {
                this.largeOrGreaterBreakpoint = false;
              }
            }
          } else {
            // Small Breakpoint
            this.isMobile = false;
            this.splitNumbers = false;
            this.largeOrGreaterBreakpoint = false;
          }
          this.cdRef.markForCheck();
        })
    );
  }

  deleteNumber(blockedNumber: BlockedE164Number): void {
    this.loadingStateComplete$.next(false);
    this.blockedNumberService
      .blockedE164NumberDelete(blockedNumber.blockedE164NumberId)
      .then(() => {
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_DELETED", { value: "Blocked number" }),
          "success"
        );
      })
      .finally(() => {
        this.loadingStateComplete$.next(true);
      });
  }

  handleDelete(blockedNumber: BlockedE164Number): void {
    const confirmModal = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      data: {
        title: `Unblock ${blockedNumber.e164Number}?`,
        message: `Are you sure you want to unblock ${blockedNumber.e164Number}?`,
        primaryBtnText: "Unblock",
        primaryBtnFlat: true,
        secondaryBtnText: "Cancel",
        noSentenceCaseTitle: true
      }
    });

    this.unsubscriber.add(
      confirmModal
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) {
            this.deleteNumber(blockedNumber);
          }
        })
    );
  }

  showSuccessSnackbar(): void {
    this.snackbar.openSnackBar("Blocked number successfully added.", "success");
  }

  showErrorSnackbar(message: string): void {
    this.snackbar.openSnackBar(message, "error");
  }

  onSubmit(): void {
    if (this.blockedNumberForm.invalid) return;
    // remove + and space from the placeholder
    const phoneNumber = this.blockedNumberForm.controls.phoneNumber.value.replace(/[+ ]/g, "");

    if (this.checkIfAlreadyExistNumber(phoneNumber)) {
      this.showErrorSnackbar("This number is already blocked.");
      return;
    }

    this.loadingStateComplete$.next(false);
    this.blockedNumberService.blockedE164NumberAdd(phoneNumber).then(res => {
      if (res.status === "success") {
        this.showSuccessSnackbar();
        this.resetForm();
      } else {
        this.showErrorSnackbar(res.data.message);
      }
      this.loadingStateComplete$.next(true);
    });
  }

  private resetForm() {
    this.blockedNumberForm.controls.phoneNumber.clearValidators();
    this.blockedNumberForm.controls.phoneNumber.setValue("");
    this.blockedNumberForm.controls.phoneNumber.setValidators([
      phoneNumberValidator(),
      Validators.required
    ]);
  }

  private checkIfAlreadyExistNumber(phoneNumber: string) {
    return !!this.blockedNumbersList.find(item => item.e164Number === phoneNumber);
  }

  private setCountryCodePlaceholder() {
    // we need the country code in the input as well to validate international numbers so going to make sure the user
    // cannot delete the country code from the input field
    this.unsubscriber.add(
      this.blockedNumberForm.controls.phoneNumber.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((value: string) => {
          const placeholder = this.countryCodePlaceholder + " ";
          if (!value.startsWith(placeholder)) {
            this.blockedNumberForm.controls.phoneNumber.patchValue(placeholder);
          }
        })
    );
  }
}
